/**
 * Build a single display string from the reservation note fields, in
 * priority order, with localized prefixes for the per-attribute notes.
 *
 * Order: general_notes -> correction_notes -> corset_notes ->
 *        strap_notes -> neckline_notes -> side_slit_notes.
 *
 * General and correction notes have no prefix (they're top-level).
 * Corset / Strap / Neckline / Side slit notes are prefixed with their
 * short localized label, e.g. "Corset: needs taking in".
 *
 * The combined string is truncated to `maxLength` characters with an
 * ellipsis when it would otherwise exceed that length.
 *
 * @param {object} record - Reservation-shaped object with the *_notes fields
 * @param {function} t - i18n translate function from useLanguage()
 * @param {object} [opts]
 * @param {number} [opts.maxLength=400] - Hard character cap for the result
 * @param {string} [opts.separator=' • '] - Separator between concatenated notes
 * @returns {string} Combined notes, or empty string if there are none
 */
export const buildReservationNotesDisplay = (record, t, opts = {}) => {
  if (!record) return '';
  const maxLength = typeof opts.maxLength === 'number' ? opts.maxLength : 400;
  const separator = typeof opts.separator === 'string' ? opts.separator : ' • ';

  const segments = [];
  const push = (value, label) => {
    if (value == null) return;
    const text = String(value).trim();
    if (!text) return;
    segments.push(label ? `${label}: ${text}` : text);
  };

  push(record.general_notes);
  push(record.correction_notes);
  push(record.corset_notes, t('reservations.corsetLabel'));
  push(record.strap_notes, t('reservations.strapLabel'));
  push(record.neckline_notes, t('reservations.necklineLabel'));
  push(record.side_slit_notes, t('reservations.sideslitLabel'));

  if (segments.length === 0) return '';

  const combined = segments.join(separator);
  if (combined.length <= maxLength) return combined;
  return `${combined.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};
